import React, { useState } from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Box,
  Button,
  Divider,
  Flex,
  Text,
} from '@chakra-ui/react';
import { Editor } from '@tinymce/tinymce-react';
import TaskForm from './TaskForm';
import { AddIcon } from '@chakra-ui/icons';
import { useUploadProjectSpec } from '../../hooks/useProjects';
import { ProjectSpec } from '../../api/projectApi';

declare global {
  interface Window {
    cloudinary: any;
  }
}

interface ProjectFormProps {
  formData: any;
  setFormData: (data: any) => void;
  projectTypes: readonly string[];
  clients: any[];
  users: any[];
  projectTasks: any[];
  setProjectTasks: (tasks: any[]) => void;
  showTaskCreation: boolean;
  setShowTaskCreation: (show: boolean) => void;
  editingProject: any;
  addTaskToProject: () => void;
  removeTaskFromProject: (index: number) => void;
  updateProjectTask: (index: number, taskData: any) => void;
  formatDateForInput: (date: Date | string | undefined | null) => string;
}

const CLOUDINARY_UPLOAD_PRESET = process.env.CLOUDINARY_UPLOAD_PRESET || 'all_files_uploads';
const CLOUDINARY_CLOUD_NAME = process.env.CLOUDINARY_CLOUD_NAME || 'dwnbctmwk';

const ProjectForm: React.FC<ProjectFormProps> = ({
  formData,
  setFormData,
  projectTypes,
  clients,
  users,
  projectTasks,
  setProjectTasks,
  showTaskCreation,
  setShowTaskCreation,
  editingProject,
  addTaskToProject,
  removeTaskFromProject,
  updateProjectTask,
  formatDateForInput,
}) => {
  const { mutateAsync: uploadProjectSpec } = useUploadProjectSpec();
  const [uploadingSpec, setUploadingSpec] = useState(false);

  const handleCloudinaryUpload = () => {
    setUploadingSpec(true);
    window.cloudinary.openUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD_NAME,
        uploadPreset: CLOUDINARY_UPLOAD_PRESET,
        sources: ['local', 'url', 'camera'],
        multiple: false,
        resourceType: 'auto',
      },
      (error: any, result: any) => {
        if (error) {
          console.error('Upload failed:', error);
          setUploadingSpec(false);
          return;
        }
        if (result.event === 'success') {
          const url = result.info.secure_url;
          setFormData({ ...formData, spec: url });
          setUploadingSpec(false);
        }
      }
    );
  };

  // Add a file to a spec's files array
  const handleCloudinarySpecUpload = (specIdx: number) => {
    setUploadingSpec(true);
    window.cloudinary.openUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD_NAME,
        uploadPreset: CLOUDINARY_UPLOAD_PRESET,
        sources: ['local', 'url', 'camera'],
        multiple: false,
        resourceType: 'auto',
        clientAllowedFormats: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'png'],
      },
      (error: any, result: any) => {
        if (error) {
          console.error('Upload failed:', error);
          setUploadingSpec(false);
          return;
        }
        if (result.event === 'success') {
          const url = result.info.secure_url;
          // Add new file object to files array
          const newSpecs = [...(formData.spec || [])];
          const filesArr = Array.isArray(newSpecs[specIdx].files) ? [...newSpecs[specIdx].files] : [];
          filesArr.push({ url, fileDescription: '' });
          newSpecs[specIdx].files = filesArr;
          setFormData({ ...formData, spec: newSpecs });
          setUploadingSpec(false);
        }
      }
    );
  };

  const handleAddSpec = () => {
    setFormData({
      ...formData,
      spec: [...(formData.spec || []), { specDescriptionText: '', files: [] }],
    });
  };

  const handleRemoveSpec = (index: number) => {
    const newSpecs = [...(formData.spec || [])];
    newSpecs.splice(index, 1);
    setFormData({ ...formData, spec: newSpecs });
  };

  const handleSpecChange = (index: number, field: keyof ProjectSpec, value: any) => {
    const newSpecs = [...(formData.spec || [])];
    newSpecs[index][field] = value;
    setFormData({ ...formData, spec: newSpecs });
  };

  // Edit file description for a file in a spec
  const handleFileDescriptionChange = (specIdx: number, fileIdx: number, desc: string) => {
    const newSpecs = [...(formData.spec || [])];
    const filesArr = Array.isArray(newSpecs[specIdx].files) ? [...newSpecs[specIdx].files] : [];
    filesArr[fileIdx] = { ...filesArr[fileIdx], fileDescription: desc };
    newSpecs[specIdx].files = filesArr;
    setFormData({ ...formData, spec: newSpecs });
  };

  // Remove a file from a spec's files array
  const handleRemoveSpecFile = (specIdx: number, fileIdx: number) => {
    const newSpecs = [...(formData.spec || [])];
    const filesArr = Array.isArray(newSpecs[specIdx].files) ? [...newSpecs[specIdx].files] : [];
    filesArr.splice(fileIdx, 1);
    newSpecs[specIdx].files = filesArr;
    setFormData({ ...formData, spec: newSpecs });
  };

  return (
    <VStack spacing={4}>
      <FormControl>
        <FormLabel>Project Name</FormLabel>
        <Input
          value={formData.projectName}
          onChange={e => setFormData({ ...formData, projectName: e.target.value })}
          placeholder="Enter project name"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Project Description</FormLabel>
        <Editor
          apiKey="gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl"
          value={formData.description || ''}
          init={{
            height: 380,
            menubar: false,
            plugins: [
              'advlist autolink lists link charmap preview anchor',
              'searchreplace visualblocks code',
              'insertdatetime table paste help wordcount'
            ],
            toolbar:
              'undo redo | formatselect | bold italic underline forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
            branding: false,
            statusbar: false,
          }}
          onEditorChange={content => setFormData({ ...formData, description: content })}
        />
      </FormControl>

      <HStack spacing={4} width="100%">
        <FormControl>
          <FormLabel>Quoted Price ($)</FormLabel>
          <Input
            type="number"
            value={formData.quotedPrice ?? ''}
            onChange={e => setFormData({ ...formData, quotedPrice: e.target.value ? parseFloat(e.target.value) : undefined })}
            placeholder="0.00"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Agreed Price ($)</FormLabel>
          <Input
            type="number"
            value={formData.agreedPrice ?? ''}
            onChange={e => setFormData({ ...formData, agreedPrice: e.target.value ? parseFloat(e.target.value) : undefined })}
            placeholder="0.00"
          />
        </FormControl>
      </HStack>
      <HStack spacing={4} width="100%">
        <FormControl>
          <FormLabel>To Begin On</FormLabel>
          <Input
            type="date"
            value={formData.toBeginOn ? formData.toBeginOn.slice(0, 10) : ''}
            onChange={e => setFormData({ ...formData, toBeginOn: e.target.value || undefined })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>To Be Completed By</FormLabel>
          <Input
            type="date"
            value={formData.toBeCompletedBy ? formData.toBeCompletedBy.slice(0, 10) : ''}
            onChange={e => setFormData({ ...formData, toBeCompletedBy: e.target.value || undefined })}
          />
        </FormControl>
      </HStack>

      <FormControl>
        <FormLabel>Project Specifications</FormLabel>
        <VStack align="stretch" spacing={4}>
          {(formData.spec || []).map((spec: ProjectSpec, idx: number) => (
            <Box key={idx} p={3} borderWidth={1} borderRadius="md" bg="gray.50">
              <FormLabel fontSize="sm">Description</FormLabel>
              <Editor
                apiKey="gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl"
                value={spec.specDescriptionText || ''}
                init={{
                  height: 150,
                  menubar: false,
                  plugins: [
                    'advlist autolink lists link charmap preview anchor',
                    'searchreplace visualblocks code',
                    'insertdatetime table paste help wordcount'
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic underline forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
                  branding: false,
                  statusbar: false,
                }}
                onEditorChange={content => handleSpecChange(idx, 'specDescriptionText', content)}
              />
              <FormLabel fontSize="sm" mt={2}>Files</FormLabel>
              <HStack align="flex-start" spacing={3}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => handleCloudinarySpecUpload(idx)}
                  isLoading={uploadingSpec}
                >
                  Add File
                </Button>
                <VStack align="flex-start" spacing={1}>
                  {Array.isArray(spec.files) && spec.files.length > 0 ? (
                    spec.files.map((fileObj: { url?: string; fileDescription?: string }, fileIdx: number) => {
                      const url = fileObj.url || '';
                      const ext = url.split('.').pop()?.toLowerCase().split('?')[0] || '';
                      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
                      const isPdf = ext === 'pdf';
                      const isDoc = ['doc', 'docx'].includes(ext);
                      return (
                        <Box key={fileIdx} mb={2} p={2} borderWidth={1} borderRadius={4} bg="white" boxShadow="sm">
                          <HStack align="flex-start" spacing={2}>
                            {isImage ? (
                              <img src={url} alt="spec preview" style={{ maxWidth: 80, borderRadius: 4 }} />
                            ) : isPdf ? (
                              <iframe src={url} title="PDF Preview" width="80px" height="100px" style={{ border: '1px solid #ccc', borderRadius: 4 }} />
                            ) : isDoc ? (
                              <Text fontSize="sm" color="gray.600">DOC/DOCX file (no preview)</Text>
                            ) : null}
                            <VStack align="flex-start" spacing={1}>
                              <Button as="a" href={url} download target="_blank" size="xs" colorScheme="blue">
                                Download
                              </Button>
                              <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleRemoveSpecFile(idx, fileIdx)}>
                                Remove File
                              </Button>
                            </VStack>
                          </HStack>
                          <Input
                            size="xs"
                            mt={1}
                            placeholder="File description (optional)"
                            value={fileObj.fileDescription || ''}
                            onChange={e => handleFileDescriptionChange(idx, fileIdx, e.target.value)}
                          />
                        </Box>
                      );
                    })
                  ) : (
                    <Text color="gray.500" fontSize="xs">No files attached</Text>
                  )}
                </VStack>
                <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleRemoveSpec(idx)}>
                  Remove Spec
                </Button>
              </HStack>
            </Box>
          ))}
          <Button size="sm" colorScheme="blue" variant="outline" onClick={handleAddSpec}>
            Add Specification
          </Button>
        </VStack>
      </FormControl>

      <HStack spacing={4} width="100%" alignItems="start">
        <FormControl>
          <FormLabel>Project Type</FormLabel>
          <Select
            value={formData.type}
            onChange={e => setFormData({ ...formData, type: e.target.value })}
          >
            {projectTypes.map(type => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Client (optional)</FormLabel>
          <Select
            value={formData.client || ''}
            onChange={e => setFormData({ ...formData, client: e.target.value })}
          >
            <option value="">No client</option>
            {clients?.map(client => (
              <option key={client._id} value={client._id}>
                {client.clientName}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Assign Users to Project</FormLabel>
          <Select
            placeholder="Select users to assign..."
            onChange={e => {
              if (e.target.value) {
                if (!(formData.assignedToUser || []).includes(e.target.value)) {
                  setFormData({
                    ...formData,
                    assignedToUser: [...(formData.assignedToUser || []), e.target.value],
                  });
                }
                e.target.value = '';
              }
            }}
          >
            {users?.map((user: any) => {
              const isSelected = (formData.assignedToUser || []).includes(user._id);
              return (
                <option key={user._id} value={user._id} disabled={isSelected}>
                  {`${user.firstName} ${user.lastName}`} {isSelected ? '(Selected)' : ''}
                </option>
              );
            })}
          </Select>
          {formData.assignedToUser && formData.assignedToUser.length > 0 && (
            <HStack mt={2} spacing={2} wrap="wrap">
              {formData.assignedToUser.map((userId: string) => {
                const user = users.find((u: any) => u._id === userId);
                return user ? (
                  <Box key={userId} px={2} py={1} bg="green.100" borderRadius="md" fontSize="sm" display="flex" alignItems="center">
                    {user.firstName} {user.lastName}
                    <Button
                      size="xs"
                      ml={2}
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          assignedToUser: (formData.assignedToUser || []).filter((id: string) => id !== userId),
                        });
                      }}
                      aria-label={`Remove ${user.firstName} ${user.lastName}`}
                    >
                      ×
                    </Button>
                  </Box>
                ) : null;
              })}
            </HStack>
          )}
        </FormControl>
        <FormControl>
          <FormLabel>Authorized By (Select Users)</FormLabel>
          <Select
            placeholder="Select users to authorize this project..."
            onChange={e => {
              if (e.target.value) {
                if (!(formData.authorizedBy || []).includes(e.target.value)) {
                  setFormData({
                    ...formData,
                    authorizedBy: [...(formData.authorizedBy || []), e.target.value],
                  });
                }
                e.target.value = '';
              }
            }}
          >
            {users?.map((user: any) => {
              const isSelected = (formData.authorizedBy || []).includes(user._id);
              return (
                <option key={user._id} value={user._id} disabled={isSelected}>
                  {`${user.firstName} ${user.lastName}`} {isSelected ? '(Selected)' : ''}
                </option>
              );
            })}
          </Select>
          {formData.authorizedBy && formData.authorizedBy.length > 0 && (
            <HStack mt={2} spacing={2} wrap="wrap">
              {formData.authorizedBy.map((userId: string) => {
                const user = users.find((u: any) => u._id === userId);
                return user ? (
                  <Box key={userId} px={2} py={1} bg="blue.100" borderRadius="md" fontSize="sm" display="flex" alignItems="center">
                    {user.firstName} {user.lastName}
                    <Button
                      size="xs"
                      ml={2}
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          authorizedBy: (formData.authorizedBy || []).filter((id: string) => id !== userId),
                        });
                      }}
                      aria-label={`Remove ${user.firstName} ${user.lastName}`}
                    >
                      ×
                    </Button>
                  </Box>
                ) : null;
              })}
            </HStack>
          )}
        </FormControl>
      </HStack>

      {/* Task Creation Section - Show for both new and editing projects */}
      <>
        <Divider my={4} />
        <Flex justify="space-between" align="center" width="full">
          <Text fontSize="lg" fontWeight="bold">
            Project Tasks
          </Text>
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={() => setShowTaskCreation(!showTaskCreation)}
          >
            {showTaskCreation ? 'Hide Task Fields' : 'Show Add Task Fields'}
          </Button>
        </Flex>
        {showTaskCreation && (
          <Box width="full" border="1px" borderColor="gray.200" borderRadius="md" bg="gray.50" p={4}>
            <VStack spacing={4}>
              {projectTasks.map((task, index) => (
                <Box
                  key={index}
                  width="full"
                  border="1px"
                  borderColor="gray.100"
                  bg={'white'}
                  borderRadius="md"
                  p={3}
                >
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontWeight="medium">Task {index + 1}</Text>
                    <HStack>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => removeTaskFromProject(index)}
                      >
                        Remove
                      </Button>
                    </HStack>
                  </Flex>
                  <VStack spacing={3} align="start">
                    <TaskForm
                      formData={task}
                      onFormChange={(taskData: any) => updateProjectTask(index, taskData)}
                      users={users}
                    />
                  </VStack>
                </Box>
              ))}
              <Button
                leftIcon={<AddIcon />}
                colorScheme='orange'
                size="md"
                onClick={addTaskToProject}
                width="full"
                py={6}
              >
                {projectTasks.length > 0 ? 'Add Another Task' : 'Add Task'}
              </Button>
            </VStack>
          </Box>
        )}
      </>
    </VStack>
  );
};

export default ProjectForm;
